<template>
  <v-container fluid>
    <page-header showBackButton></page-header>

    <v-card class="mb-4">
      <v-card-text>
        <v-form ref="basicForm" @submit.prevent>
          <v-row class="ma-0">
            <v-col cols="12" sm="6" lg="3">
              <label class="text-subtitle-2 mb-2 d-block colorGrey--text">{{ $t('registerDate') }}</label>
              <div class="colorBlack--text">{{ $Formatter.displayCreateDate(formData.create_date) }}</div>
            </v-col>
            <v-col cols="12" sm="6" lg="3">
              <label class="text-subtitle-2 mb-2 d-block colorGrey--text">{{ $t('phone') }}</label>
              <div class="colorBlack--text">
                ({{ formData.zone }}) {{ formData.phone }}
                <v-chip small v-if="!formData.verify" class="ml-1" color="error" outlined label>{{
                  $t('identityVerifyOptions.unverified')
                }}</v-chip>
                <v-chip small v-if="formData.verify" class="ml-1" color="success" outlined label>{{
                  $t('identityVerifyOptions.verified')
                }}</v-chip>
              </div>
            </v-col>
            <v-col cols="12" sm="6" lg="3">
              <label class="text-subtitle-2 mb-2 d-block colorGrey--text">{{ $t('email') }}</label>
              <div class="colorBlack--text">{{ $Formatter.displayCreateDate(formData.email) || '-' }}</div>
            </v-col>
            <v-col cols="12" sm="6" lg="3">
              <label class="text-subtitle-2 mb-2 d-block colorGrey--text">{{ $t('knownFrom') }}</label>
              <div class="colorBlack--text" v-if="formData.known_from">
                {{ $t(`knownFromOption.${formData.known_from}`) }}
              </div>
              <div class="colorBlack--text" v-else>-</div>
            </v-col>
            <v-col cols="12" sm="6" lg="3">
              <form-select label="status" :fieldValue.sync="formData.status" :options="$statusOptions" required />
            </v-col>
          </v-row>
          <v-btn depressed elevation="0" class="ml-2 mb-4" color="primary" type="button" @click="save()" :loading="$store.getters.isLoading">{{
            $t('save')
          }}</v-btn>
        </v-form>
      </v-card-text>
    </v-card>

    <v-card class="mb-8">
      <v-card-text>
        <v-row class="ma-0">
          <v-col cols="12" class="d-flex justify-space-between align-center">
            <h4 class="section-title">{{ $t('company') }}</h4>
            <v-btn
              depressed
              class="ml-2 mb-4"
              elevation="0"
              color="primary"
              type="button"
              :disabled="$store.getters.isLoading"
              :to="{ name: 'CreateCompany', params: { uid: id } }"
              >{{ $t('menu.addCompany') }}</v-btn
            >
          </v-col>

          <v-col cols="12">
            <datatable
              :isLoading="tableLoading"
              :tableHeaders="tableHeaders"
              :tableData="tableData"
              actionViewRouteLink="CompanyDetail"
              disableToolbar
              disablePagination
              disableShadow
              @delete-clicked="deleteCompany($event)"
            >
              <template v-slot:[`item.type`]="{ item }">{{ item.type | formatCompanyType }}</template>
            </datatable>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    <confirm-dialog ref="confirmRemove" />
  </v-container>
</template>

<script>
import FormSelect from '@/components/form/FormSelect.vue'
import Datatable from '@/components/Datatable.vue'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
export default {
  name: 'UserDetail',
  components: {
    FormSelect,
    Datatable,
    ConfirmDialog,
  },
  computed: {
    id() {
      const id = parseInt(this.$route.params.id)
      if (!isNaN(id)) {
        return id
      }

      return null
    },
  },
  data: vm => ({
    formData: {
      create_date: '',
      zone: '',
      phone: '',
      known_from: '',
      status: 'active',
      verify: false,
      email: '',
    },

    tableLoading: true,
    tableData: [],
    tableHeaders: [
      { value: 'create_date', text: vm.$t('createDate'), width: 140 },
      { value: 'type', text: vm.$t('companyType') },
      { value: 'name_en', text: vm.$t('companyNameEn') },
      { value: 'name_zh', text: vm.$t('companyNameZh') },
      { value: 'br', text: vm.$t('brExpiredDate'), width: 130 },
      { value: 'nar1', text: vm.$t('nar1ExpiredDate'), width: 130 },
      { value: 'actionViewDelete', text: '', align: 'end' },
    ],
  }),
  methods: {
    async getUser() {
      try {
        const data = await this.$Fetcher.GetUserById(this.id)
        for (let key in this.formData) {
          if (key !== 'known_from' && this.$validate.DataValid(data[key])) {
            this.formData[key] = data[key]
          }
        }

        if (data.known_from && data.known_from.length > 0) {
          this.formData.known_from = data.known_from[0]
        } else {
          this.formData.known_from = ''
        }

        this.getUserCompanies()
      } catch (err) {
        this.$common.error(err)
        this.$store.dispatch('setAlert', {
          message: 'noData',
          redirect: 'UserList',
        })
      } finally {
        this.$store.dispatch('setInitLoading', false)
      }
    },

    async getUserCompanies() {
      this.tableLoading = true
      try {
        const data = await this.$Fetcher.GetUserCompanies(this.id)
        this.tableData = data.map(item => {
          return {
            id: item.id,
            create_date: item.create_date,
            type: item.type,
            name_en: item.name_en,
            name_zh: item.name_zh,
            br: item.br?.expiry.substring(0, 10),
            nar1: item.nar?.expiry.substring(0, 10),
          }
        })
      } catch {
        this.tableData = []
      } finally {
        this.tableLoading = false
      }
    },

    async save() {
      if (this.$store.getters.isLoading) {
        this.$store.dispatch('toggleSnack', {
          message: 'processing',
        })
        return
      }

      const valid = await this.$refs['basicForm'].validate()
      if (!valid) {
        this.$store.dispatch('toggleSnack', {
          message: 'required',
        })
        return
      }

      this.$store.dispatch('setLoading', true)

      try {
        await this.$Fetcher.UpdateUser(this.id, {
          status: this.formData.status,
        })
        this.$store.dispatch('setDataIsUpdated', false)
        this.$store.dispatch('setLoading', false)
        this.$store.dispatch('toggleSnack', {
          type: 'success',
          message: 'message.saveSuccess',
          refresh: true,
        })
      } catch (err) {
        this.$common.error(err)
        this.$store.dispatch('setLoading', false)
        this.$store.dispatch('setAlert', {
          title: 'message.saveFail',
          message: err,
          type: 'error',
        })
      }
    },

    // --- 刪除公司
    async deleteCompany(id) {
      if (this.$store.getters.isLoading) {
        this.$store.dispatch('toggleSnack', {
          message: 'processing',
        })
        return
      }
      const confirm = await this.$refs['confirmRemove'].show()
      if (confirm) {
        this.$store.dispatch('setLoading', true)
        try {
          await this.$Fetcher.DeleteCompany(id)
          this.$store.dispatch('setLoading', false)
          this.$store.dispatch('setDataIsUpdated', false)
          this.$store.dispatch('toggleSnack', {
            type: 'success',
            message: 'message.saveSuccess',
          })
          this.getUserCompanies();
        } catch (err) {
          this.$common.error(err)
          this.$store.dispatch('setLoading', false)
          this.$store.dispatch('setAlert', {
            title: 'message.saveFail',
            message: err,
            type: 'error',
          })
        }
      }
    },
  },

  async mounted() {
    this.$store.dispatch('setInitLoading', true)
    if (this.$validate.DataValid(this.id)) {
      this.getUser()
    } else {
      this.$store.dispatch('setInitLoading', false)
      this.$store.dispatch('setAlert', {
        message: 'noData',
        redirect: 'UserList',
      })
    }
  },
}
</script>
